import React, { useState } from "react";
import PropTypes from "prop-types";
import StatusBadge from "../../../shared/StatusBadge";
import SectionHeader from "./SectionHeader";
import OpendateTrackingPixelRow from "./OpendateTrackingPixelRow";
import PixelTrackingModal from "./PixelTrackingModal";
import MetaCapiConnectionRow from "./MetaCapiConnectionRow";

const PIXEL_CONFIG = [
  {
    name: 'Google Analytics',
    description: 'Website and event page analytics',
    icon: 'fab fa-google',
    color: '#4285F4',
    field: 'google_tracking_id',
    placeholder: 'UA-12345678 or G-XXXXXXXX',
  },
  {
    name: 'Facebook',
    description: 'Conversion tracking and remarketing',
    icon: 'fab fa-facebook-f',
    color: '#1877F2',
    field: 'facebook_pixel_id',
    placeholder: '12345678',
  },
  {
    name: 'Reddit',
    description: 'Conversion tracking and audience targeting',
    icon: 'fab fa-reddit-alien',
    color: '#FF4500',
    field: 'reddit_pixel_id',
    placeholder: 'a1_abc0d',
  },
  {
    name: 'TikTok',
    description: 'Conversion tracking and audience targeting',
    icon: 'fab fa-tiktok',
    color: 'var(--sl-text-primary, #000000)',
    field: 'tiktok_pixel_id',
    placeholder: 'C4XXXXXXXXXXXXXXXXXX',
  },
  {
    name: 'Spotify',
    description: 'Conversion tracking and audience targeting',
    icon: 'fab fa-spotify',
    color: '#1DB954',
    field: 'spotify_pixel_id',
    placeholder: 'Pixel ID',
  },
  {
    name: 'X',
    description: 'Conversion tracking and audience targeting',
    icon: 'fab fa-twitter',
    color: 'var(--sl-text-primary, #000000)',
    field: 'x_pixel_id',
    placeholder: 'o8z2x',
    eventFieldsDescription: 'X tracks page views with the pixel ID alone. To track purchases, add to cart, or checkout, '
      + 'create each event in X Events Manager with Define with code, then paste its event ID here.',
    eventFields: [
      { field: 'x_purchase_event_id', label: 'Purchase event ID', placeholder: 'tw-o8z2x-o8z5c' },
      { field: 'x_add_to_cart_event_id', label: 'Add to cart event ID', placeholder: 'tw-o8z2x-o8z5d' },
      {
        field: 'x_checkout_event_id',
        label: 'Checkout initiated event ID',
        placeholder: 'tw-o8z2x-o8z5e',
        hint: 'Create this as a Custom event in X, not Added payment info. It fires before the fan enters card details.',
      },
    ],
  },
];

const pixelFieldNames = (pixel) => [
  pixel.field,
  ...(pixel.eventFields || []).map((eventField) => eventField.field),
];

const PixelCoverageBadge = ({ trackedCount, venueCount }) => {
  if (trackedCount === 0) return null;

  if (venueCount === 1) return <StatusBadge status="connected" label="Tracking" />;

  return (
    <StatusBadge
      status={trackedCount === venueCount ? 'connected' : 'pending'}
      label={`${trackedCount} of ${venueCount} venues`}
    />
  );
};

PixelCoverageBadge.propTypes = {
  trackedCount: PropTypes.number.isRequired,
  venueCount: PropTypes.number.isRequired,
};

const ALL_VENUES = 'all';

const VenueSelectorRow = ({ venueOwnerships, selectedVenueOwnershipId, onVenueChange }) => {
  const editingAllVenues = selectedVenueOwnershipId === ALL_VENUES;

  return (
    <tr>
      <td
        colSpan={3}
        style={{
          paddingLeft: 24,
          paddingRight: 24,
          paddingTop: 16,
          paddingBottom: 16,
        }}
      >
        <div style={{ textAlign: 'left' }}>
          <label className="team-form-label" htmlFor="pixel-tracking-venue-ownership">
            Editing pixels for
          </label>
          <select
            id="pixel-tracking-venue-ownership"
            className="team-form-select"
            style={{ maxWidth: 320 }}
            value={selectedVenueOwnershipId || ''}
            onChange={(e) => onVenueChange(
              e.target.value === ALL_VENUES ? ALL_VENUES : parseInt(e.target.value, 10)
            )}
          >
            {venueOwnerships.map((venueOwnership) => (
              <option key={venueOwnership.id} value={venueOwnership.id}>
                {venueOwnership.name}
              </option>
            ))}
            {venueOwnerships.length > 1 && (
              <option value={ALL_VENUES}>
                All {venueOwnerships.length} venues
              </option>
            )}
          </select>
          <p
            style={{
              margin: '8px 0 0',
              fontSize: 13,
              color: 'var(--sl-text-quaternary, #717680)',
              lineHeight: 1.5,
            }}
          >
            {editingAllVenues
              ? `Saving will overwrite the pixel on all ${venueOwnerships.length} venues, replacing any
                 pixel a venue already has.`
              : 'The pixels below apply only to shows at this venue. Each venue needs its own pixel to track its shows.'}
          </p>
        </div>
      </td>
    </tr>
  );
};

VenueSelectorRow.propTypes = {
  venueOwnerships: PropTypes.array.isRequired,
  selectedVenueOwnershipId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onVenueChange: PropTypes.func.isRequired,
};

const sharedValueAcross = (venues, field) => {
  const values = venues.map((venue) => venue[field] || '');
  return values.every((value) => value === values[0]) ? values[0] : '';
};

const PixelTrackingSection = ({ venues, onVenuesChange, selectedVenueOwnershipId, teamId }) => {
  const [selectedId, setSelectedId] = useState(
    selectedVenueOwnershipId || (venues[0] && venues[0].id)
  );
  const [activeModal, setActiveModal] = useState(null);

  const editingAllVenues = selectedId === ALL_VENUES;
  const activePixel = PIXEL_CONFIG.find((pixel) => pixel.field === activeModal);
  const selectedVenueOwnership = venues.find((venue) => venue.id === selectedId);
  const hasTarget = editingAllVenues ? venues.length > 0 : Boolean(selectedVenueOwnership);

  const modalTarget = (pixel) => {
    if (!editingAllVenues) return selectedVenueOwnership;

    return {
      id: ALL_VENUES,
      name: `all ${venues.length} venues`,
      venueCount: venues.length,
      hasExistingValue: venues.some((venue) => Boolean(venue[pixel.field])),
      ...Object.fromEntries(
        pixelFieldNames(pixel).map((field) => [field, sharedValueAcross(venues, field)])
      ),
    };
  };

  const handleVenueChange = (nextId) => {
    setActiveModal(null);
    setSelectedId(nextId);
  };

  const handleSaved = (savedValues, refreshedVenues, savedVenueOwnershipId) => {
    if (refreshedVenues) {
      if (Array.isArray(refreshedVenues)) onVenuesChange(refreshedVenues);
      return;
    }

    onVenuesChange(venues.map((venue) => (
      venue.id === savedVenueOwnershipId ? { ...venue, ...savedValues } : venue
    )));
  };

  return (
    <>
      <SectionHeader title="Pixel Tracking" />
      <OpendateTrackingPixelRow />
      {venues.length > 0 && (
        <VenueSelectorRow
          venueOwnerships={venues}
          selectedVenueOwnershipId={selectedId}
          onVenueChange={handleVenueChange}
        />
      )}
      {PIXEL_CONFIG.map((pixel) => {
        const currentValue = editingAllVenues
          ? sharedValueAcross(venues, pixel.field)
          : selectedVenueOwnership && selectedVenueOwnership[pixel.field];
        const trackedCount = venues.filter((venue) => venue[pixel.field]).length;

        return (
          <tr key={pixel.name}>
            <td style={{ paddingLeft: 24 }}>
              <div className="team-member-info">
                <div
                  className="team-avatar"
                  style={{
                    borderRadius: 8,
                    backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <i className={pixel.icon} style={{ fontSize: 20, color: pixel.color }} />
                </div>
                <div className="team-member-details">
                  <span className="team-member-name">{pixel.name}</span>
                  <span className="team-member-email">{pixel.description}</span>
                </div>
              </div>
            </td>
            <td>
              <PixelCoverageBadge trackedCount={trackedCount} venueCount={venues.length} />
            </td>
            <td style={{ textAlign: 'right', paddingRight: 24 }}>
              <button
                className="team-btn-secondary"
                style={{ fontSize: 13, padding: '4px 12px' }}
                disabled={!hasTarget}
                onClick={() => setActiveModal(pixel.field)}
              >
                {currentValue ? 'Edit' : 'Configure'}
              </button>
            </td>
          </tr>
        );
      })}
      {venues.length > 0 && (
        <MetaCapiConnectionRow
          venues={venues}
          selectedVenueOwnership={selectedVenueOwnership}
          editingAllVenues={editingAllVenues}
          teamId={teamId}
          onVenuesChange={onVenuesChange}
        />
      )}
      {activeModal && hasTarget && (
        <tr>
          <td colSpan={3} style={{ padding: 0 }}>
            <PixelTrackingModal
              key={`${selectedId}-${activeModal}`}
              pixel={activePixel}
              venueOwnership={modalTarget(activePixel)}
              teamId={teamId}
              onClose={() => setActiveModal(null)}
              onSaved={handleSaved}
            />
          </td>
        </tr>
      )}
    </>
  );
};

PixelTrackingSection.propTypes = {
  venues: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
  })),
  onVenuesChange: PropTypes.func.isRequired,
  selectedVenueOwnershipId: PropTypes.number,
  teamId: PropTypes.number.isRequired,
};

PixelTrackingSection.defaultProps = {
  venues: [],
};

export default PixelTrackingSection;
